// src/components/mdx/client/visualizations/creativeFatigueChartData.ts

/**
 * Shared data utilities for the Creative Fatigue chart.
 *
 * These helpers encapsulate the expensive data generation logic so the React
 * component can memoise the results and avoid recomputing during pointer
 * events. They also allow unit tests to exercise the behaviour directly by
 * providing deterministic random number generators.
 */

/**
 * Shape of a single creative fatigue data point.
 * 
 * @property {number} exposure - The exposure index (day number or impression count)
 * @property {number} roas - Return on Ad Spend value for this data point
 * @property {number} [movingAverage] - Optional moving average value calculated over a window
 */
export interface CreativeFatigueDataPoint {
  exposure: number
  roas: number
  movingAverage?: number
}

/**
 * Deterministic random helper used for tests. Components call the function
 * without passing an RNG so we default to Math.random at runtime.
 */
type RandomSource = () => number

/**
 * Generates simulated ad performance data showing creative fatigue patterns.
 * 
 * The generated data follows a realistic pattern with four main phases:
 * 1. Stable performance (35%) - Natural variations around baseline ROAS of 4
 * 2. False dip (10%) - Temporary performance drop that recovers
 * 3. Recovery (10%) - Return to baseline performance
 * 4. Creative fatigue (45%) - Progressive decline in four sub-phases:
 *    - Initial dip: Subtle 8% drop
 *    - Accelerated decline: Moderate decline (0.12-0.16 per day)
 *    - Gradual decline: Main phase with occasional plateaus
 *    - Final decline: Re-accelerated drop to minimum values
 *
 * @param {number} numPoints - Number of data points to generate (typically 100 for a 100-day timeline)
 * @param {RandomSource} [rng=Math.random] - Optional random number generator for deterministic tests. Defaults to Math.random for production use.
 * @returns {CreativeFatigueDataPoint[]} Array of data points with exposure index and ROAS values
 */
export function generateCreativeFatigueData(
  numPoints: number,
  rng: RandomSource = Math.random
): CreativeFatigueDataPoint[] {
  const data: CreativeFatigueDataPoint[] = []

  // Phase durations (as percentages of total timeline)
  const stablePhase = 0.35 // 35% - Stable period
  const falseDipPhase = 0.10 // 10% - False dip
  const recoveryPhase = 0.10 // 10% - Recovery
  const fatiguePhase = 0.45 // 45% - Creative fatigue
  void fatiguePhase

  // Calculate phase boundaries
  const stablePhaseEnd = Math.floor(numPoints * stablePhase) // Day 35
  const falseDipPhaseEnd = Math.floor(numPoints * (stablePhase + falseDipPhase)) // Day 45
  const recoveryPhaseEnd = Math.floor(
    numPoints * (stablePhase + falseDipPhase + recoveryPhase)
  ) // Day 55

  // Fatigue sub-phases (as percentages of fatigue phase)
  const initialDipPhase = 0.10 // 10% of fatigue phase - Initial dip (subtle)
  const acceleratedDeclinePhase = 0.15 // 15% of fatigue phase - Initial accelerated decline
  const gradualDeclinePhase = 0.65 // 65% of fatigue phase - Main phase (gradual decline)
  const finalDeclinePhase = 0.10 // 10% of fatigue phase - Final phase (re-accelerated decline)
  void finalDeclinePhase

  // Calculate fatigue sub-phase boundaries
  const fatiguePhaseLength = numPoints - recoveryPhaseEnd
  const initialDipEnd = recoveryPhaseEnd + Math.floor(fatiguePhaseLength * initialDipPhase) // Around day 60
  const acceleratedDeclineEnd =
    initialDipEnd + Math.floor(fatiguePhaseLength * acceleratedDeclinePhase) // Around day 67
  const gradualDeclineEnd =
    acceleratedDeclineEnd + Math.floor(fatiguePhaseLength * gradualDeclinePhase) // Around day 90
  // Final phase goes from gradualDeclineEnd to numPoints (days 90-100)

  // Starting ROAS value
  let baseRoas = 4

  for (let i = 0; i < numPoints; i++) {
    // Stable performance with natural variations and slight uptrend at the end
    if (i < stablePhaseEnd) {
      // Create natural ups and downs that average out around 4
      // Use sine waves with different frequencies for more natural variation
      const progress = i / stablePhaseEnd
      const uptrend = progress > 0.7 ? (progress - 0.7) * 0.5 : 0 // Slight uptrend before the dip

      baseRoas =
        4 +
        Math.sin(i * 0.4) * 0.6 + // Primary wave
        Math.sin(i * 0.8) * 0.3 + // Secondary wave
        (rng() - 0.5) * 0.8 + // Random noise
        uptrend // Slight uptrend before the dip
    }
    // False dip - moderate but noticeable
    else if (i < falseDipPhaseEnd) {
      // Calculate how far we are into the false dip phase (0-1)
      const falseDipProgress = (i - stablePhaseEnd) / (falseDipPhaseEnd - stablePhaseEnd)

      // Create a more gradual dip that doesn't crash
      const dipSeverity = falseDipProgress * 2.0 // Maximum dip of 2.0 ROAS points
      baseRoas = 4.2 - dipSeverity + (rng() - 0.5) * 0.4 // Start from slightly higher than 4
    }
    // Recovery from false dip - returns to average, not exceeding it
    else if (i < recoveryPhaseEnd) {
      // Calculate how far we are into the recovery phase (0-1)
      const recoveryProgress = (i - falseDipPhaseEnd) / (recoveryPhaseEnd - falseDipPhaseEnd)

      // Recover back to around 4.0 (the stable average)
      const startPoint = 2.6 // Where the false dip ended
      const targetPoint = 4.0 // Where we want to recover to

      baseRoas = startPoint + (targetPoint - startPoint) * recoveryProgress + (rng() - 0.5) * 0.4
    }
    // Fatigue Phase 1: Initial dip (subtle) - Days ~55-60
    else if (i < initialDipEnd) {
      // First point in fatigue phase - ensure it's lower than the last recovery point but not too steep
      if (i === recoveryPhaseEnd) {
        baseRoas = baseRoas * 0.92 // 8% initial drop - more subtle than before
      } else {
        // Small continued dip with some variance
        const dipProgress = (i - recoveryPhaseEnd) / (initialDipEnd - recoveryPhaseEnd)
        // Smaller decline factor (0.10-0.15) for more subtle initial dip
        baseRoas -= 0.10 + dipProgress * 0.05 + (rng() - 0.5) * 0.15
      }
    }
    // Fatigue Phase 2: Initial accelerated decline - Days ~60-67
    else if (i < acceleratedDeclineEnd) {
      const acceleratedProgress = (i - initialDipEnd) / (acceleratedDeclineEnd - initialDipEnd)
      // Moderate decline in this phase (0.12-0.16 per day) with increased variance
      baseRoas -= 0.12 + acceleratedProgress * 0.04 + (rng() - 0.5) * 0.3
    }
    // Fatigue Phase 3: Main phase (gradual decline) - Days ~67-90
    else if (i < gradualDeclineEnd) {
      const gradualProgress = (i - acceleratedDeclineEnd) / (gradualDeclineEnd - acceleratedDeclineEnd)

      // More gradual decline with occasional small plateaus and higher variance
      if (rng() > 0.8) {
        // 20% chance of a small plateau or minor recovery
        baseRoas += rng() * 0.2 - 0.05
      } else {
        // 80% chance of continued decline with varying severity
        // Smaller decline factor (0.05-0.09) for more gradual decline
        const declineFactor = 0.05 + gradualProgress * 0.04
        baseRoas -= declineFactor
      }

      // Add more pronounced variance for natural-looking decline
      baseRoas += (rng() - 0.5) * 0.3

      // Ensure we don't crash below 1.0 during this phase
      if (baseRoas < 1.0) {
        baseRoas = 1.0 + rng() * 0.2
      }
    }
    // Fatigue Phase 4: Final phase (re-accelerated decline) - Days ~90-100
    else {
      const finalProgress = (i - gradualDeclineEnd) / (numPoints - gradualDeclineEnd)

      // Re-accelerated decline without restraint (0.15-0.25 per day)
      baseRoas -= 0.15 + finalProgress * 0.1 + (rng() - 0.5) * 0.2

      // Only enforce minimum of 0.8 in this final phase
      if (baseRoas < 0.8) {
        // Allow it to drop below 0.8 as we approach the very end (last ~3 days)
        if (finalProgress > 0.7) {
          baseRoas = Math.max(0.3, 0.8 - (finalProgress - 0.7) * 1.5)
        } else {
          baseRoas = 0.8 + rng() * 0.1
        }
      }
    }

    // Clamp between 0 and 7
    const finalRoas = Math.max(0, Math.min(7, baseRoas))

    data.push({
      exposure: i,
      roas: finalRoas
    })
  }

  return data
}

/**
 * Calculates moving average for the data series.
 * 
 * Applies a sliding window moving average to smooth out short-term fluctuations
 * and highlight longer-term trends in the ROAS data. Each point's moving average
 * is calculated from all points within the window ending at that point.
 *
 * @param {CreativeFatigueDataPoint[]} data - Raw data points to calculate moving average for
 * @param {number} windowSize - Size of the moving average window (number of points to average). A larger window produces smoother results.
 * @returns {CreativeFatigueDataPoint[]} New array with moving average values added to each data point
 */
export function calculateCreativeFatigueMovingAverage(
  data: CreativeFatigueDataPoint[],
  windowSize: number
): CreativeFatigueDataPoint[] {
  return data.map((point, index) => {
    const startIndex = Math.max(0, index - windowSize + 1)
    const window = data.slice(startIndex, index + 1)
    const sum = window.reduce((acc, val) => acc + val.roas, 0)

    return {
      ...point,
      movingAverage: sum / window.length
    }
  })
}